/** @jsx jsx */
/** @jsxFrag */
import React, { type MutableRefObject, memo, type ReactNode, useState } from 'react';
import { css, keyframes, jsx, cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useCellHoverCallbacks } from '../controllers/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../constants.tsx';

type MinimumWidth = 'narrow';

export type Props = {
	content?: ReactNode;
	children: ReactNode;
	contentRef?: MutableRefObject<HTMLElement | null>;
	minimumWidth?: MinimumWidth;
	entryPointActions?: EntryPointActions;
	isMoreItems?: boolean;
	shouldGetParentRef?: boolean;
};

export const HoverPopover = memo(
	({
		content,
		children,
		contentRef,
		minimumWidth,
		entryPointActions,
		isMoreItems,
		shouldGetParentRef,
	}: Props) => {
		const [isPopupOpen, setIsPopupOpen] = useState(false);

		useCellHoverCallbacks({
			contentRef,
			onPreload: entryPointActions?.preload,
			onLoad: entryPointActions?.load,
			onPopupToggle: setIsPopupOpen,
			shouldGetParentRef,
		});

		if (!content) {
			return children;
		}

		const contentWithPopover = (
			<UFOSegment name="jira-list-hover-popover">
				<JSErrorBoundary
					id="jira-list-hover-popover"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					fallback="flag"
				>
					{children}
					{content && (
						<div
							css={[
								containerStyles,
								containerVariantStyles[minimumWidth ?? 'content'],
								isMoreItems ? moreItemsTop : normalTop,
							]}
						>
							<div
								data-testid="hover-popover.ui.hover-popover-content"
								aria-hidden
								css={popoverStyles}
							>
								{content}
							</div>
						</div>
					)}
				</JSErrorBoundary>
			</UFOSegment>
		);

		return isPopupOpen ? contentWithPopover : children;
	},
);

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const containerStyles = css({
	paddingTop: token('space.050'),
	position: 'absolute',
	top: '40px',
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	zIndex: 9999,
});

const normalTop = css({
	top: '40px',
});

const moreItemsTop = css({
	top: '20px',
});

const containerVariantStyles = cssMap({
	narrow: {
		minWidth: '200px',
		maxWidth: '300px',
	},
	content: {
		whiteSpace: 'nowrap',
	},
});

const popoverStyles = css({
	backgroundColor: token('elevation.surface.overlay'),
	paddingInline: token('space.150'),
	paddingBlock: token('space.075'),
	borderRadius: token('border.radius'),
	boxShadow: token('elevation.shadow.overlay'),
	fontBody: token('font.body'),
	fontWeight: token('font.weight.regular'),
	overflowWrap: 'break-word',
	maxHeight: '300px',
	overflowY: 'scroll',
});
